import { BarsOutlined, CloudUploadOutlined, CoffeeOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, Col, Divider, Flex, Image, Input, Row, Spin, Steps } from 'antd';
import { observer } from "mobx-react";
import React, { ClipboardEvent, useEffect } from "react";
import { useParams } from 'react-router-dom';
import { useStore } from '../../stores/root-store';
import SourceFilter from '../common/filters/source-filter';
import TagsEditor from '../common/filters/tags-editor';
import ImageUploader from '../common/image-uploader';
import NumberInKimWithTheme from '../common/number-in-kim-with-theme';
import EditTaskStore from '../../stores/task/edit-task';
import handleKeybordPhotoPaste from '../common/photo-paste-helper';
import Subjects from '../../models/subjects';

const DeleteableImage: React.FC<{ photoId: string, isMainPhoto: boolean, task: EditTaskStore }> = ({ photoId, isMainPhoto, task }) => {

    function handleDelete(): void {
        task.deletePhoto(photoId, isMainPhoto);
    }

    return (
        <Flex gap="small" style={{ paddingBottom: "10px" }}>
            <Image width={300} src={task.getUrl(photoId)} />
            <Button icon={<DeleteOutlined />} onClick={handleDelete} title='Удалить изображение' />
        </Flex>
    );
}

const EditTaskPanel: React.FC<{subject: Subjects}> = ({subject}) => {

    const { taskId } = useParams();
    const { authStore, subjectStores } = useStore();
    const editTask = subjectStores[subject].editTask;

    useEffect(() => {
        if (authStore.isAuthenticated) {
            editTask.numberInKimWithTheme.loadDictionaries().then(() => editTask.loadTask(Number.parseInt(taskId || '')));
        }
    }, [authStore.isAuthenticated, subjectStores, editTask, taskId, editTask.numberInKimWithTheme]
    );

    const tagsStore = { data: editTask.tags };
    const sourceStore = { source: editTask.source };
    const numberInKimWithTheme = { numberWithTheme: editTask.numberInKimWithTheme };
    const photoUploaderStore = { uploader: editTask.photoUploader };
    const solutionUploaderStore = { uploader: editTask.solutionUploader };

    const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
        handleKeybordPhotoPaste(event, photoUploaderStore.uploader, solutionUploaderStore.uploader);
    }

    const handleOnChangeStep = (value: number) => {
        editTask.changeStep(value);
        editTask.photoUploader.changeActivated(value === 1);
        editTask.solutionUploader.changeActivated(value === 2);
    }

    const handleChangeAnswer = (event: React.ChangeEvent<HTMLInputElement>) => {
        editTask.changeAnswer(event.target.value);
    }

    const handleChangeText = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        editTask.changeText(event.target.value);
    }

    const handleClickSave = () => {
        editTask.save();
    }

    const getPhotoImages = (photoIds: string[] | undefined, isMainPhotos: boolean) => {
        return (photoIds || []).map(id => <DeleteableImage key={id} {...{ photoId: id, isMainPhoto: isMainPhotos, task: editTask }} />);
    }

    const spinBlock = <Row gutter={8}>
        <Col span={8} className="center-block">
            <Spin size='large' />
        </Col>
    </Row>;

    const { TextArea } = Input;

    const editTaskBlock = <Row gutter={8}>
        {editTask.task?.id &&
            <Col span={8}>
                <SourceFilter {...sourceStore} />
                <NumberInKimWithTheme {...numberInKimWithTheme} />
                <TagsEditor {...tagsStore} />
                <Input placeholder='ответ' onChange={handleChangeAnswer} style={{ marginTop: "10px", marginBottom: "10px" }}
                    value={editTask.answer} />
                <TextArea placeholder='текст' onChange={handleChangeText} value={editTask.text} autoSize={{ minRows: 4, maxRows: 10 }} />
                <Button key="enter" className="save__button" type="primary" onClick={handleClickSave}>Сохранить</Button>
            </Col>
        }
        <Col span={8}>
            <ImageUploader {...photoUploaderStore} />
            <Divider>Загруженные изображения</Divider>
            <div style={{ marginTop: "15px" }}><Image.PreviewGroup>{getPhotoImages(editTask.task?.photoIds, true)}</Image.PreviewGroup></div>
        </Col>
        <Col span={8}>
            <ImageUploader {...solutionUploaderStore} />
            <Divider>Загруженные изображения</Divider>
            <div style={{ marginTop: "15px" }}><Image.PreviewGroup>{getPhotoImages(editTask.task?.solutionPhotoIds, false)}</Image.PreviewGroup></div>
        </Col>
    </Row>;

    return (
        <div onPaste={handlePaste}>
            <Steps
                current={editTask.step}
                className="steps__block"
                type="navigation"
                onChange={handleOnChangeStep}
            >
                <Steps.Step title="Данные" icon={<BarsOutlined />} />
                <Steps.Step title="Файлы задачи" icon={<CloudUploadOutlined />} />
                <Steps.Step title="Файлы решения" icon={<CoffeeOutlined />} />
            </Steps>
            {editTask.loading ? spinBlock : editTaskBlock}
        </div>
    );
}

export default observer(EditTaskPanel);
